import React from 'react';
import { risiko, risikoStufe } from '../../../services/sifa/gbuLogic';
import { RISIKO_TON, S_STUFEN, W_STUFEN, klemmeStufe, matrixZeilen } from './gbuModulLogic';

/** R als farbiges Kaestchen (gruen/gelb/rot nach der 3×3-Matrix). R wird gerechnet, nie getippt. */
export const RisikoBadge: React.FC<{ w: number; s: number; titel?: string }> = ({ w, s, titel }) => {
  const r = risiko(klemmeStufe(w), klemmeStufe(s));
  const ton = RISIKO_TON[risikoStufe(r)];
  return (
    <span
      title={titel ? `${titel}: R = ${w} × ${s} = ${r} (${ton.label})` : `R = ${r} (${ton.label})`}
      className={`inline-flex items-center justify-center min-w-[2.25rem] h-7 px-1.5 text-sm font-black border rounded-lg ${ton.klasse}`}
    >
      {r}
    </span>
  );
};

interface Props {
  titel: string;
  w: number;
  s: number;
  onChange?: (w: number, s: number) => void;
  disabled?: boolean;
}

const FELD = 'w-full min-h-[40px] px-2 text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 disabled:text-slate-500';

/** W und S je 1–3 mit dem gerechneten R daneben — „vorher" oder „nachher". */
export const RisikoWahl: React.FC<Props> = ({ titel, w, s, onChange, disabled = false }) => (
  <div>
    <p className="text-[11px] font-bold uppercase text-slate-500 mb-1">{titel}</p>
    <div className="flex items-end gap-2">
      <label className="flex-1 min-w-0">
        <span className="block text-[10px] text-slate-500">W (Wahrscheinlichkeit)</span>
        <select value={klemmeStufe(w)} disabled={disabled || !onChange} onChange={(e) => onChange?.(Number(e.target.value), s)} className={FELD}>
          {W_STUFEN.map((x) => <option key={x.wert} value={x.wert}>{x.label}</option>)}
        </select>
      </label>
      <label className="flex-1 min-w-0">
        <span className="block text-[10px] text-slate-500">S (Schadensschwere)</span>
        <select value={klemmeStufe(s)} disabled={disabled || !onChange} onChange={(e) => onChange?.(w, Number(e.target.value))} className={FELD}>
          {S_STUFEN.map((x) => <option key={x.wert} value={x.wert}>{x.label}</option>)}
        </select>
      </label>
      <div className="shrink-0 pb-1.5"><RisikoBadge w={w} s={s} titel={titel} /></div>
    </div>
  </div>
);

/** Legende der Risikomatrix: R = W × S; 1–2 gering, 3–4 mittel, 6–9 hoch. */
export const MatrixLegende: React.FC<{ kompakt?: boolean }> = ({ kompakt = false }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap items-start gap-4">
    <table className="text-[11px] border-separate" style={{ borderSpacing: 3 }}>
      <tbody>
        {matrixZeilen(risikoStufe).map((z) => (
          <tr key={z.s}>
            <td className="pr-1 text-slate-500 font-bold text-right">S {z.s}</td>
            {z.zellen.map((c) => (
              <td key={c.w} className={`w-8 h-7 text-center font-black rounded ${RISIKO_TON[c.stufe].zelle}`}>{c.r}</td>
            ))}
          </tr>
        ))}
        <tr>
          <td />
          {[1, 2, 3].map((w) => <td key={w} className="text-center text-slate-500 font-bold">W {w}</td>)}
        </tr>
      </tbody>
    </table>
    {!kompakt && (
      <div className="text-xs text-slate-600 space-y-1 min-w-[12rem] flex-1">
        <p className="font-bold text-slate-700">Risiko R = W × S</p>
        <p><span className="inline-block w-3 h-3 rounded-sm align-middle mr-1 bg-emerald-200" /> 1–2 gering: Maßnahmen beibehalten</p>
        <p><span className="inline-block w-3 h-3 rounded-sm align-middle mr-1 bg-amber-200" /> 3–4 mittel: Maßnahmen prüfen, verbessern</p>
        <p><span className="inline-block w-3 h-3 rounded-sm align-middle mr-1 bg-red-300" /> 6–9 hoch: sofort handeln — R nachher ≥ 6 wird hervorgehoben</p>
      </div>
    )}
  </div>
);
